import { useEffect, useRef, useState } from "react";
import { X, Radio, Ear, Eye } from "lucide-react";

/**
 * Admin-side live-view modal. Connects to the signaling relay as "viewer"
 * and asks the employee's dashboard tab to turn on ONLY the media this mode
 * needs — "audio" (Supervise = mic only) or "video" (Watch = camera only).
 * No media ever touches our backend — it's a direct peer-to-peer connection
 * once signaling completes.
 */
export function SupervisionViewer({ employeeId, employeeName, mode = "audio", onClose }) {
  const mediaRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const [status, setStatus] = useState("connecting"); // connecting | waiting | live | error | ended

  useEffect(() => {
    let aborted = false;
    const wsUrl = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/api/ws/supervision/${employeeId}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
    pcRef.current = pc;

    pc.ontrack = (e) => {
      if (aborted) return;
      if (mediaRef.current) mediaRef.current.srcObject = e.streams[0];
      setStatus("live");
    };
    pc.onicecandidate = (e) => {
      if (e.candidate && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "ice-candidate", candidate: e.candidate }));
      }
    };
    pc.onconnectionstatechange = () => {
      if (!aborted && ["failed", "closed", "disconnected"].includes(pc.connectionState)) setStatus("ended");
    };

    ws.onopen = () => {
      if (aborted) return;
      setStatus("waiting");
      ws.send(JSON.stringify({ type: mode === "video" ? "watch-request" : "listen-request" }));
    };
    ws.onmessage = async (evt) => {
      if (aborted) return;
      const msg = JSON.parse(evt.data);
      if (msg.type === "offer") {
        await pc.setRemoteDescription({ type: "offer", sdp: msg.sdp });
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        ws.send(JSON.stringify({ type: "answer", sdp: answer.sdp }));
      } else if (msg.type === "ice-candidate" && msg.candidate) {
        try { await pc.addIceCandidate(msg.candidate); } catch { /* ignore */ }
      }
    };
    ws.onerror = () => { if (!aborted) setStatus("error"); };
    ws.onclose = () => { if (!aborted) setStatus((s) => (s === "live" ? "ended" : s)); };

    return () => {
      aborted = true;
      try { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "stop-media" })); } catch { /* noop */ }
      try { ws.close(); } catch { /* noop */ }
      try { pc.close(); } catch { /* noop */ }
    };
  }, [employeeId, mode]);

  const statusText = {
    connecting: "Connecting…",
    waiting: mode === "video" ? "Turning on their camera…" : "Turning on their mic…",
    live: "Live", error: "Could not connect", ended: "Session ended",
  }[status];

  return (
    <div data-testid="supervision-viewer-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.6)" }}>
      <div className="rounded-2xl p-5 w-full max-w-lg" style={{ background: "var(--bg-card)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {mode === "video"
              ? <Eye className={`w-4 h-4 ${status === "live" ? "animate-pulse" : ""}`} style={{ color: status === "live" ? "#c45a3c" : "var(--text-secondary)" }} />
              : <Ear className={`w-4 h-4 ${status === "live" ? "animate-pulse" : ""}`} style={{ color: status === "live" ? "#c45a3c" : "var(--text-secondary)" }} />}
            <span className="font-serif-display text-lg">{mode === "video" ? "Watching" : "Supervising"} {employeeName}</span>
          </div>
          <button data-testid="supervision-viewer-close" onClick={onClose} className="p-1"><X className="w-5 h-5" /></button>
        </div>
        {mode === "video" ? (
          <div className="rounded-xl overflow-hidden aspect-video flex items-center justify-center" style={{ background: "#111" }}>
            <video ref={mediaRef} autoPlay playsInline className="w-full h-full object-contain" data-testid="supervision-video" />
          </div>
        ) : (
          <div className="rounded-xl p-8 flex items-center justify-center gap-3" style={{ background: "var(--bg-alt)" }}>
            <Radio className={`w-8 h-8 ${status === "live" ? "animate-pulse" : ""}`} style={{ color: status === "live" ? "#c45a3c" : "var(--text-secondary)" }} />
            <span className="text-sm" style={{ color: "var(--text-secondary)" }}>Audio-only — listening to their mic</span>
            <audio ref={mediaRef} autoPlay data-testid="supervision-audio" />
          </div>
        )}
        <div className="text-xs mt-2" data-testid="supervision-status" style={{ color: "var(--text-secondary)" }}>{statusText}</div>
      </div>
    </div>
  );
}
